/**
 * useDocumentCache - Hook para persistencia de sesión del wizard PTEL
 * 
 * Evita pérdida de trabajo guardando estado en sessionStorage:
 * - Paso actual y pasos completados
 * - Datos de extracción (infraestructuras, stats, metadata)
 * - Datos procesados
 * 
 * Incluye warning en beforeunload si hay trabajo sin guardar.
 * 
 * @version 1.0.0
 * @module hooks/useDocumentCache
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { ExtractionResult } from '../components/Step1'
import type { ExtractedInfrastructure, ExtractionStats, DocumentMetadata } from '../types/processing'
import type { AlmacenDecisiones } from './useDecisionesFila'
import type { AlmacenEdiciones } from './useEdicionesP2'

// Key para sessionStorage
const CACHE_KEY = 'ptel-wizard-cache'
// C0-e2 (DOP-1 a″) y C0-e3 (DOP-1 a): `decisiones` y `ediciones` son campos
// añadidos, no un cambio incompatible:
// la versión no sube. Un bundle viejo servido por el service worker lo ignora y
// conserva la sesión; con otra versión la borraría entera. Subirla solo para
// cambios que el código anterior no pueda leer.
const CACHE_VERSION = 1

/**
 * Datos serializables del ExtractionResult (sin File)
 */
export interface SerializableExtractionData {
  infrastructures: ExtractedInfrastructure[]
  stats: ExtractionStats
  metadata: Omit<DocumentMetadata, 'processedAt'> & { processedAt: string }
  formatAnalysis?: ExtractionResult['formatAnalysis']
  /** Nombre del archivo original (para referencia) */
  fileName: string
  /** Tamaño del archivo original */
  fileSize: number
}

/**
 * Estado del wizard que se persiste en cache
 */
export interface WizardCacheState {
  version: number
  timestamp: string
  currentStep: number
  completedSteps: number[]
  extractionData: SerializableExtractionData | null
  processedData: unknown | null
  /**
   * C0-e2: lo decidido en P3 (`useDecisionesFila`), por id. Una caché escrita
   * antes de C0-e2 no lo trae y se lee como `{}`. Se valida al reponer.
   */
  decisiones: AlmacenDecisiones
  /**
   * C0-e3: lo corregido en P2 (`useEdicionesP2`), por id de fila. Una caché
   * escrita antes de C0-e3 no lo trae y se lee como `{}`. Se valida al reponer.
   */
  ediciones: AlmacenEdiciones
}

/**
 * Resultado del hook useDocumentCache
 */
export interface UseDocumentCacheResult {
  /** Si hay datos en cache */
  hasCache: boolean
  /** Si hay cambios sin guardar desde última persistencia */
  isDirty: boolean
  /** Datos del cache (si existen) */
  cachedState: WizardCacheState | null
  /** Guardar estado actual */
  saveState: (state: Partial<Omit<WizardCacheState, 'version' | 'timestamp'>>) => void
  /** Restaurar estado desde cache */
  restoreState: () => WizardCacheState | null
  /** Limpiar cache */
  clearCache: () => void
  /** Marcar como guardado (no dirty) */
  markAsSaved: () => void
  /** Convertir ExtractionResult a formato serializable */
  toSerializable: (data: ExtractionResult) => SerializableExtractionData
  /**
   * Inverso de toSerializable: reconstruye un ExtractionResult desde la caché
   * (CACHREC). El File original no se persiste; se reconstruye vacío con el
   * nombre y el tamaño guardados, suficiente para los consumidores actuales
   * (solo leen `file.name`).
   */
  fromSerializable: (data: SerializableExtractionData) => ExtractionResult
}

/**
 * Convierte ExtractionResult (con File) a formato serializable
 */
function extractionToSerializable(data: ExtractionResult): SerializableExtractionData {
  return {
    infrastructures: data.infrastructures,
    stats: data.stats,
    metadata: {
      ...data.metadata,
      processedAt: data.metadata.processedAt instanceof Date 
        ? data.metadata.processedAt.toISOString()
        : data.metadata.processedAt
    },
    formatAnalysis: data.formatAnalysis,
    fileName: data.file.name,
    fileSize: data.file.size
  }
}

/**
 * Reconstruye ExtractionResult desde el formato serializable (CACHREC).
 * `processedAt` vuelve a ser Date; el File se recrea con el nombre original
 * y un cuerpo de `fileSize` bytes vacíos para conservar `file.size`.
 */
function extractionFromSerializable(data: SerializableExtractionData): ExtractionResult {
  const { fileName, fileSize, metadata, ...rest } = data
  return {
    ...rest,
    file: new File([new Uint8Array(Math.max(0, fileSize || 0))], fileName),
    metadata: {
      ...metadata,
      processedAt: new Date(metadata.processedAt)
    }
  }
}

/**
 * FECHAREC: `processedData` se guarda tal cual y JSON devuelve `processedAt`
 * como texto. Al leer la caché vuelve a ser Date, igual que en
 * `extractionFromSerializable`. Una fecha ilegible se deja como está: la
 * resuelve `toExportMetadata` con aviso (DOP-2 a′).
 */
function rehidratarProcessedData(data: unknown): unknown {
  if (!data || typeof data !== 'object') return data
  const metadata = (data as { metadata?: unknown }).metadata
  if (!metadata || typeof metadata !== 'object') return data
  const processedAt = (metadata as { processedAt?: unknown }).processedAt
  if (typeof processedAt !== 'string') return data
  const fecha = new Date(processedAt)
  if (Number.isNaN(fecha.getTime())) return data
  return { ...data, metadata: { ...metadata, processedAt: fecha } }
}

/**
 * Guarda estado en sessionStorage de forma segura
 */
function saveToStorage(state: WizardCacheState): boolean {
  try {
    const serialized = JSON.stringify(state)
    sessionStorage.setItem(CACHE_KEY, serialized)
    return true
  } catch (error) {
    console.warn('[useDocumentCache] Error guardando en sessionStorage:', error)
    return false
  }
}

/**
 * Lee estado desde sessionStorage de forma segura
 */
function loadFromStorage(): WizardCacheState | null {
  try {
    const serialized = sessionStorage.getItem(CACHE_KEY)
    if (!serialized) return null
    
    const state = JSON.parse(serialized) as WizardCacheState
    
    // Validar versión
    if (state.version !== CACHE_VERSION) {
      console.warn('[useDocumentCache] Versión de cache incompatible, descartando')
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    
    return {
      ...state,
      processedData: rehidratarProcessedData(state.processedData),
      // C0-e2: caché de antes de C0-e2 → sin decisiones. Lo que venga se valida al reponer.
      decisiones: state.decisiones ?? {},
      // C0-e3: caché de antes de C0-e3 → sin ediciones. Lo que venga se valida al reponer.
      ediciones: state.ediciones ?? {},
    }
  } catch (error) {
    console.warn('[useDocumentCache] Error leyendo sessionStorage:', error)
    return null
  }
}

/**
 * Limpia el cache de sessionStorage
 */
function clearStorage(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY)
  } catch (error) {
    console.warn('[useDocumentCache] Error limpiando sessionStorage:', error)
  }
}

/**
 * Hook para persistencia de sesión del wizard PTEL
 * 
 * @param enableBeforeUnload - Si se debe mostrar warning al cerrar (default: true)
 * @returns Objeto con funciones y estado del cache
 * 
 * @example
 * ```tsx
 * const { hasCache, saveState, restoreState, clearCache, isDirty } = useDocumentCache()
 * 
 * // Al iniciar, restaurar estado si existe
 * useEffect(() => {
 *   const cached = restoreState()
 *   if (cached) {
 *     setCurrentStep(cached.currentStep)
 *     setCompletedSteps(cached.completedSteps)
 *   }
 * }, [])
 * 
 * // Al cambiar paso, guardar
 * useEffect(() => {
 *   saveState({ currentStep, completedSteps, extractionData })
 * }, [currentStep, extractionData])
 * ```
 */
export function useDocumentCache(enableBeforeUnload = true): UseDocumentCacheResult {
  const [cachedState, setCachedState] = useState<WizardCacheState | null>(() => loadFromStorage())
  const [isDirty, setIsDirty] = useState(false)
  const lastSavedRef = useRef<string>('')

  // Calcular si hay cache
  const hasCache = cachedState !== null && (
    cachedState.currentStep > 1 || 
    cachedState.completedSteps.length > 0 ||
    cachedState.extractionData !== null
  )

  // Guardar estado
  const saveState = useCallback((
    partialState: Partial<Omit<WizardCacheState, 'version' | 'timestamp'>>
  ) => {
    const currentCache = loadFromStorage()
    
    const newState: WizardCacheState = {
      version: CACHE_VERSION,
      timestamp: new Date().toISOString(),
      currentStep: partialState.currentStep ?? currentCache?.currentStep ?? 1,
      completedSteps: partialState.completedSteps ?? currentCache?.completedSteps ?? [],
      extractionData: partialState.extractionData !== undefined 
        ? partialState.extractionData 
        : currentCache?.extractionData ?? null,
      processedData: partialState.processedData !== undefined
        ? partialState.processedData
        : currentCache?.processedData ?? null,
      decisiones: partialState.decisiones ?? currentCache?.decisiones ?? {},
      ediciones: partialState.ediciones ?? currentCache?.ediciones ?? {}
    }

    const saved = saveToStorage(newState)
    if (saved) {
      setCachedState(newState)
      lastSavedRef.current = newState.timestamp
      setIsDirty(false)
    }
  }, [])

  // Restaurar estado
  const restoreState = useCallback((): WizardCacheState | null => {
    const state = loadFromStorage()
    setCachedState(state)
    setIsDirty(false)
    return state
  }, [])

  // Limpiar cache
  const clearCache = useCallback(() => {
    clearStorage()
    setCachedState(null)
    setIsDirty(false)
    lastSavedRef.current = ''
  }, [])

  // Marcar como guardado
  const markAsSaved = useCallback(() => {
    setIsDirty(false)
  }, [])

  // Convertir a serializable
  const toSerializable = useCallback((data: ExtractionResult): SerializableExtractionData => {
    return extractionToSerializable(data)
  }, [])

  // Reconstruir desde serializable (CACHREC)
  const fromSerializable = useCallback((data: SerializableExtractionData): ExtractionResult => {
    return extractionFromSerializable(data)
  }, [])

  // Warning en beforeunload
  useEffect(() => {
    if (!enableBeforeUnload) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Solo mostrar warning si hay trabajo significativo
      const cache = loadFromStorage()
      if (cache && (cache.extractionData || cache.processedData)) {
        e.preventDefault()
        // Mensaje estándar del navegador (el texto personalizado ya no funciona en navegadores modernos)
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [enableBeforeUnload])

  return {
    hasCache,
    isDirty,
    cachedState,
    saveState,
    restoreState,
    clearCache,
    markAsSaved,
    toSerializable,
    fromSerializable
  }
}

export default useDocumentCache
